import React, { useState, useEffect } from "react";
import { faqAdminAPI } from "../../api/apiService";
import { Plus, Edit2, Trash2, X, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";

const FAQManagement = () => {
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ question: "", answer: "", is_active: true, order: 0 });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchFAQs();
    }, []);

    const fetchFAQs = async () => {
        try {
            setLoading(true);
            const res = await faqAdminAPI.getAllFAQs();
            setFaqs(res.data);
        } catch (error) {
            toast.error("Failed to fetch FAQs");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleCreateNew = () => {
        setFormData({ question: "", answer: "", is_active: true, order: 0 });
        setEditingId(null);
        setFormOpen(true);
    };

    const handleEdit = (faq) => {
        setFormData({
            question: faq.question,
            answer: faq.answer,
            is_active: faq.is_active,
            order: faq.order,
        });
        setEditingId(faq.id);
        setFormOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
        try {
            await faqAdminAPI.deleteFAQ(id);
            toast.success("FAQ deleted successfully");
            fetchFAQs();
        } catch (error) {
            toast.error("Failed to delete FAQ");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.question.trim() || !formData.answer.trim()) {
            toast.error("Please fill in both question and answer");
            return;
        }

        try {
            setIsSubmitting(true);
            if (editingId) {
                await faqAdminAPI.updateFAQ(editingId, formData);
                toast.success("FAQ updated successfully");
            } else {
                await faqAdminAPI.createFAQ(formData);
                toast.success("FAQ created successfully");
            }
            setFormOpen(false);
            fetchFAQs();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to save FAQ");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">FAQ Management</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Create and edit FAQs displayed on the public website.
                    </p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={fetchFAQs}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-200"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                    <button
                        onClick={handleCreateNew}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 shadow-md shadow-indigo-500/20 transition-all font-medium"
                    >
                        <Plus className="w-4 h-4" />
                        Add FAQ
                    </button>
                </div>
            </div>

            {loading && !formOpen ? (
                <div className="flex justify-center items-center h-64">
                    <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
                </div>
            ) : faqs.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 text-center flex flex-col items-center justify-center min-h-[400px]">
                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700/50 rounded-full flex items-center justify-center mb-4">
                        <Plus className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No FAQs found</h3>
                    <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto mb-6">
                        Get started by creating your first frequently asked question.
                    </p>
                    <button
                        onClick={handleCreateNew}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
                    >
                        Create FAQ
                    </button>
                </div>
            ) : (
                <div className="grid gap-4">
                    {faqs.map((faq) => (
                        <div key={faq.id} className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${faq.is_active ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>
                                        {faq.is_active ? 'Active' : 'Hidden'}
                                    </span>
                                    <span className="text-xs text-slate-500 dark:text-slate-400">Order: {faq.order}</span>
                                </div>
                                <h3 className="text-lg font-medium text-slate-900 dark:text-white">{faq.question}</h3>
                                <p className="text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{faq.answer}</p>
                            </div>
                            <div className="flex gap-2 w-full sm:w-auto mt-4 sm:mt-0 pt-4 sm:pt-0 border-t border-slate-100 sm:border-0 dark:border-slate-700">
                                <button
                                    onClick={() => handleEdit(faq)}
                                    className="flex-1 sm:flex-none p-2 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 transition-colors flex justify-center items-center gap-2"
                                >
                                    <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleDelete(faq.id)}
                                    className="flex-1 sm:flex-none p-2 rounded-lg bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 transition-colors flex justify-center items-center"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal Form */}
            {formOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                                {editingId ? "Edit FAQ" : "Add New FAQ"}
                            </h3>
                            <button
                                onClick={() => setFormOpen(false)}
                                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 dark:hover:text-slate-300 transition-colors"
                                disabled={isSubmitting}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        Question *
                                    </label>
                                    <input
                                        type="text"
                                        name="question"
                                        value={formData.question}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 dark:focus:ring-indigo-400/50"
                                        placeholder="e.g. How does billing work?"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        Answer *
                                    </label>
                                    <textarea
                                        name="answer"
                                        value={formData.answer}
                                        onChange={handleInputChange}
                                        rows="6"
                                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 dark:focus:ring-indigo-400/50"
                                        placeholder="Enter the comprehensive answer here..."
                                        required
                                    ></textarea>
                                </div>

                                <div className="flex gap-6">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Display Order
                                        </label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 dark:focus:ring-indigo-400/50"
                                        />
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Lower numbers appear first.</p>
                                    </div>
                                    <div className="flex items-center gap-2 mt-6">
                                        <input
                                            type="checkbox"
                                            id="is_active"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleInputChange}
                                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-700"
                                        />
                                        <label htmlFor="is_active" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Active (Visible publicly)
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 pt-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setFormOpen(false)}
                                    className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors font-medium"
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg transition-all font-medium disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2 shadow-md shadow-indigo-500/20"
                                >
                                    {isSubmitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                                    {editingId ? "Save Changes" : "Create FAQ"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FAQManagement;
